import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

// Grace period before a hover-out closes the submenu, so briefly crossing the
// gap between the launcher and the bar doesn't snap it shut. Mirrors the
// primary header menu's deactivate delay.
const CLOSE_DELAY = 250;

/**
 * A single top-level item of the secondary header menu that has child links.
 *
 * Reuses the primary header menu's submenu markup and CSS (`.menu-list__…` +
 * `mega-menu-list`), so the dropdown bar is visually identical to the primary
 * menu's. This component just drives the state the primary CSS reacts to:
 *   - `aria-expanded` on the launcher toggles visibility (and the content fade),
 *   - `--submenu-height` animates the clip-path reveal and the shadow bar height,
 *   - `--submenu-opacity` fades the shadow bar in/out.
 * It stands in for `header-menu.js` (which is coupled to the overflow-list and
 * would fill the header's right column). Hover opens for mouse; click / Enter /
 * Space toggles for keyboard & touch; Escape and click-outside close.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The disclosure launcher.
 * @property {HTMLElement} submenu - The submenu bar (measured for its height).
 *
 * @extends {Component<Refs>}
 */
class SecondaryMenuItem extends Component {
  requiredRefs = ['button', 'submenu'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#closeTimer);
    document.removeEventListener('click', this.#handleClickOutside);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /**
   * Toggles the bar. Bound declaratively to the launcher's click, so it fires
   * for mouse clicks, touch taps and keyboard Enter/Space.
   *
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  };

  open() {
    clearTimeout(this.#closeTimer);
    if (this.isOpen) return;

    // Close any sibling that's still open so only one bar shows at a time —
    // otherwise moving between top-level items leaves the previous bar lingering
    // on its close delay, overlapping the new one. The primary menu avoids this
    // because a single component owns all its items.
    this.#closeSiblings();

    this.setAttribute('open', '');
    this.refs.button?.setAttribute('aria-expanded', 'true');

    const { submenu } = this.refs;
    if (submenu instanceof HTMLElement) {
      this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
    }
    this.style.setProperty('--submenu-opacity', '1');

    document.addEventListener('click', this.#handleClickOutside);
  }

  close = () => {
    clearTimeout(this.#closeTimer);
    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');

    document.removeEventListener('click', this.#handleClickOutside);
  };

  // Hover reveal for pointer devices only; touch taps are handled by the click
  // toggle so they don't open then immediately re-toggle.
  #onPointerEnter = (event) => {
    if (event.pointerType === 'touch') return;
    this.open();
  };

  #onPointerLeave = (event) => {
    if (event.pointerType === 'touch') return;
    // Delay the close so a brief excursion across the launcher/bar gap doesn't
    // snap the dropdown shut; a re-entering pointerenter cancels it via open().
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(this.close, CLOSE_DELAY);
  };

  // Close when focus leaves the component entirely (keyboard tab-through).
  #onFocusOut = (event) => {
    if (!this.contains(/** @type {Node | null} */ (event.relatedTarget))) {
      this.close();
    }
  };

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.close();
      this.refs.button?.focus();
    }
  };

  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) this.close();
  };

  #closeSiblings() {
    const scope = this.closest('.header-secondary-menu') ?? document;
    for (const el of scope.querySelectorAll('secondary-menu-item')) {
      if (el !== this && el instanceof SecondaryMenuItem && el.isOpen) el.close();
    }
  }
}

if (!customElements.get('secondary-menu-item')) {
  customElements.define('secondary-menu-item', SecondaryMenuItem);
}
